import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Phone, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1577634670297-f7700eabf3fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxtb3ZpbmclMjB0cnVjayUyMGhpZ2h3YXklMjByb2FkJTIwcmVsb2NhdGlvbiUyMHRydWNrfGVufDB8fHx8MTc5MTc0MjgxNHww&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "New place.", accent: false },
  { text: "Fresh start.", accent: true },
  { text: "We move the rest.", accent: false },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section className="hero" id="home" ref={ref}>
      <div className="hero-grain" aria-hidden="true" />
      <div className="container hero-grid">
        <div className="hero-copy">
          <motion.p className="overline" data-testid="hero-overline"
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}>
            Transport · Packers &amp; Movers · Car Carriers
          </motion.p>
          <h1 data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span className="line-mask" key={line.text}>
                <motion.span
                  className={line.accent ? "accent" : ""}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.28 + i * 0.14, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p className="hero-text" data-testid="hero-subtext"
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}>
            From the first wrapped box to the last mile, Jay Maa Sherawali carries your world — home, office or car — from Patna to anywhere in India.
          </motion.p>
          <motion.div className="hero-actions"
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}>
            <a data-testid="hero-quote-button" className="btn btn-gold" href="#quote">Calculate my estimate <ArrowRight size={16} /></a>
            <a data-testid="hero-call-button" className="btn btn-ghost" href="tel:+919135667888"><Phone size={15} /> 91356 67888</a>
          </motion.div>
          <motion.div className="hero-status" data-testid="hero-status-badge"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.9 }}>
            <span className="pulse-dot" /> DISPATCH READY — PATNA HUB
            <span className="status-divider" />
            OPEN 7 DAYS A WEEK
          </motion.div>
        </div>

        <motion.div className="hero-visual"
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}>
          <div className="hero-frame">
            <motion.img style={{ y: imgY }} data-testid="hero-moving-image" src={HERO_IMG} alt="Jay Maa Sherawali moving truck on the highway" />
          </div>
          <motion.div className="float-card route-card" style={{ y: cardY }} data-testid="route-card">
            <span className="route-dot" />
            <div><small>ON THE MOVE</small><strong>Patna → Bengaluru</strong></div>
            <Check size={16} />
          </motion.div>
          <motion.div className="float-card stat-card" data-testid="hero-stat-card"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}>
            <span className="stat-icon"><ShieldCheck size={18} /></span>
            <div><strong>15,000+</strong><small>safe relocations</small></div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
